import React, { useEffect, useState } from "react";
import Layout from "../../components/ui/Layout/Layout";
import Button from "../../components/ui/Button/Button";
import Snackbar from "../../components/ui/Snackbar/Snackbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import "./profilePage.css";

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [name, setName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [snackbar, setSnackbar] = useState(null);

  // Eye toggle states
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setProfile(data);
        setName(data.name);
        setImagePreview(data.image || "/default.jpg");
      } else {
        setSnackbar({ message: data.message || "Failed to fetch profile", type: "error" });
      }
    } catch (err) {
      setSnackbar({ message: "Failed to fetch profile", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handlePasswordUpdate = async () => {
    if (!currentPassword) {
      setSnackbar({ message: "Current password is required", type: "error" });
      return;
    }
    if (currentPassword === newPassword) {
      setSnackbar({ message: "New password cannot be the same as your current password", type: "error" });
      return;
    }
    if (newPassword.length < 6) {
      setSnackbar({ message: "New password must be at least 6 characters", type: "error" });
      return;
    }
    if (newPassword !== confirmPassword) {
      setSnackbar({ message: "Passwords do not match", type: "error" });
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/password`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (res.ok) {
        setSnackbar({ message: data.message || "Password updated successfully", type: "success" });
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
      } else {
        setSnackbar({ message: data.message, type: "error" });
      }
    } catch (err) {
      setSnackbar({ message: "Failed to update password", type: "error" });
    }
  };

  const handleProfileUpdate = async () => {
    if (!name) {
      setSnackbar({ message: "Name is required", type: "error" });
      return;
    }
    setLoading(true);
    try {
      const form = new FormData();
      form.append("name", name);
      if (imageFile) form.append("image", imageFile);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await res.json();
      if (res.ok) {
        setProfile(data.user || data);
        setEditMode(false);
        setImageFile(null);
        setSnackbar({ message: "Profile updated successfully", type: "success" });
      } else {
        setSnackbar({ message: data.message || "Failed to update profile", type: "error" });
      }
    } catch (err) {
      setSnackbar({ message: "Failed to update profile", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, []);

  if (loading || !profile) return <Layout>Loading...</Layout>;

  return (
    <Layout hideNavbar>
      <div className="profile-page">
        <h2>My Profile</h2>

        {/* PROFILE IMAGE */}
        <div className="profile-image-section">
          <img src={imagePreview || "/default.jpg"} alt="Profile" className="profile-image" />
          <p className={`user-role role-${profile.role}`}>{profile.role.toUpperCase()}</p>
          {editMode && (
            <label className="change-image-btn">
              Change Image
              <input type="file" accept="image/*" hidden onChange={handleImageChange} />
            </label>
          )}
        </div>

        {/* PERSONAL DETAILS */}
        <div className="profile-section">
          <h4>Personal Details</h4>

          <div className="profile-field">
            <label>Name:</label>
            {editMode ? (
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength="30" />
            ) : (
              <span>{profile.name}</span>
            )}
          </div>

          <div className="profile-field">
            <label>Email:</label>
            <span>{profile.email}</span>
          </div>

          {profile.role !== "admin" && (
            <div className="profile-field">
              <label>Job Title:</label>
              <span>{profile.jobTitle}</span>
            </div>
          )}

          <div className="profile-field">
            <label>Shop:</label>
            <span>{profile.shopName}</span>
          </div>

          <div className="profile-actions">
            {editMode ? (
              <>
                <Button variant="primary" onClick={handleProfileUpdate} isLoading={loading}>
                  Save Changes
                </Button>
                <Button variant="text" onClick={() => {
                  setEditMode(false);
                  setImageFile(null);
                  setImagePreview(profile.image || "/default.jpg");
                  setName(profile.name);
                }}>
                  Cancel
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => setEditMode(true)}>
                Edit Profile
              </Button>
            )}
          </div>
        </div>

        {/* PASSWORD SECTION */}
        <div className="profile-section password-section">
          <h4>Change Password</h4>

          {editMode ? (
            <>
              <div className="profile-field">
                <label>Current Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showCurrent ? "text" : "password"}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter current password"
                  />
                  <button
  type="button"
  className="eye-toggle"
  onClick={() => setShowCurrent(!showCurrent)}
  tabIndex={-1}
>
  <FontAwesomeIcon icon={showCurrent ? faEyeSlash : faEye} />
</button>
                </div>
              </div>

              <div className="profile-field">
                <label>New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showNew ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                  />
                  <button type="button" className="eye-toggle" onClick={() => setShowNew(!showNew)} tabIndex={-1}>
                    <FontAwesomeIcon icon={showNew ? "eye-slash" : "eye"} />
                  </button>
                </div>
              </div>

              <div className="profile-field">
                <label>Confirm New Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirm ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                  />
                  <button type="button" className="eye-toggle" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}>
                    <FontAwesomeIcon icon={showConfirm ? "eye-slash" : "eye"} />
                  </button>
                </div>
              </div>

              <Button variant="danger" onClick={handlePasswordUpdate}>
                Update Password
              </Button>
            </>
          ) : (
            <p className="password-placeholder">••••••••</p>
          )}
        </div>

        {snackbar && (
          <Snackbar message={snackbar.message} type={snackbar.type} onClose={() => setSnackbar(null)} />
        )}
      </div>
    </Layout>
  );
};

export default ProfilePage;
